/* section-orsa-policies-tabs.js
   ORSA — Policies hub (robust + best performance)
   - Lazy-load active tab
   - Cache in sessionStorage
   - Idle-prefetch others
   - Try ?sections=main first, fallback to full HTML
   - Shows real error reason (404 etc.) in the pane
*/

(() => {
  "use strict";

  const hub = document.querySelector("[data-orsa-policies-hub]");
  if (!hub) return;

  const panes = Array.from(hub.querySelectorAll("[data-pane]"));
  const links = Array.from(hub.querySelectorAll("[data-tab-link]"));

  const allowed = ["shipping", "return", "privacy", "cookies"];
  const allowedSet = new Set(allowed);

  const hubPath = hub.getAttribute("data-hub-path") || "/pages/policies";
  const sectionsId = hub.getAttribute("data-sections-id") || "main";
  const contentSelector = hub.getAttribute("data-content-selector") || ".orsa-legal__content";

  const urls = {
    shipping: hub.getAttribute("data-shipping-url") || "/pages/shipping",
    return: hub.getAttribute("data-return-url") || "/pages/returns",
    privacy: hub.getAttribute("data-privacy-url") || "/pages/privacy",
    cookies: hub.getAttribute("data-cookies-url") || "/pages/cookies",
  };

  const cacheKey = (tab) => `orsa:policiesHub:v3:${tab}`;

  const getTab = () => {
    const url = new URL(window.location.href);
    const tab = (url.searchParams.get("tab") || "").toLowerCase();
    return allowedSet.has(tab) ? tab : "shipping";
  };

  const setActiveUI = (tab) => {
    panes.forEach((p) => {
      p.style.display = p.getAttribute("data-pane") === tab ? "block" : "none";
    });
    links.forEach((a) => {
      a.classList.toggle("is-active", a.getAttribute("data-tab-link") === tab);
    });
  };

  const setBusy = (tab, busy) => {
    const pane = hub.querySelector(`[data-pane="${tab}"]`);
    if (pane) pane.setAttribute("aria-busy", busy ? "true" : "false");
  };

  const inject = (tab, html) => {
    const pane = hub.querySelector(`[data-pane="${tab}"]`);
    if (pane) pane.innerHTML = html;
  };

  const parseContentFromHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const content = doc.querySelector(contentSelector);
    return content ? content.innerHTML : null;
  };

  const fetchText = async (url) => {
    const res = await fetch(url, { credentials: "same-origin" });
    const text = await res.text();
    return { res, text };
  };

  const fetchViaSections = async (baseUrl) => {
    const sep = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${sep}sections=${encodeURIComponent(sectionsId)}`;

    const { res, text } = await fetchText(url);
    if (!res.ok) return { ok: false, status: res.status, statusText: res.statusText, inner: null, used: "sections" };

    const ct = (res.headers.get("content-type") || "").toLowerCase();

    // Parse JSON if it is JSON (or looks like it)
    const looksJson = ct.includes("application/json") || text.trim().startsWith("{");
    if (!looksJson) return { ok: false, status: 200, statusText: "Non-JSON response", inner: null, used: "sections" };

    let json;
    try {
      json = JSON.parse(text);
    } catch {
      return { ok: false, status: 200, statusText: "Invalid JSON", inner: null, used: "sections" };
    }

    const sectionHtml = json && json[sectionsId];
    if (!sectionHtml) return { ok: false, status: 200, statusText: `Missing "${sectionsId}"`, inner: null, used: "sections" };

    const inner = parseContentFromHtml(sectionHtml);
    return { ok: !!inner, status: 200, statusText: inner ? "OK" : `Missing ${contentSelector}`, inner, used: "sections" };
  };

  const fetchViaHtml = async (baseUrl) => {
    const { res, text } = await fetchText(baseUrl);
    if (!res.ok) return { ok: false, status: res.status, statusText: res.statusText, inner: null, used: "html" };

    const inner = parseContentFromHtml(text);
    return { ok: !!inner, status: 200, statusText: inner ? "OK" : `Missing ${contentSelector}`, inner, used: "html" };
  };

  const loadTab = async (tab, { force = false } = {}) => {
    if (!allowedSet.has(tab)) return;

    if (!force) {
      const cached = sessionStorage.getItem(cacheKey(tab));
      if (cached) {
        inject(tab, cached);
        setBusy(tab, false);
        return;
      }
    }

    setBusy(tab, true);

    try {
      const baseUrl = urls[tab];
      if (!baseUrl) throw new Error(`Missing URL for ${tab}`);

      // 1) sections fast-path
      let result = await fetchViaSections(baseUrl);

      // 2) fallback to HTML if sections fails
      if (!result.ok) result = await fetchViaHtml(baseUrl);

      if (!result.ok || !result.inner) {
        inject(
          tab,
          `<p>Could not load this policy right now. (${baseUrl} — ${result.used} — ${result.status} ${result.statusText})</p>`
        );
        return;
      }

      inject(tab, result.inner);
      sessionStorage.setItem(cacheKey(tab), result.inner);
    } catch (err) {
      inject(tab, `<p>Could not load this policy right now.</p>`);
      // console.error("[ORSA policies hub]", tab, err);
    } finally {
      setBusy(tab, false);
    }
  };

  const prefetchIdle = (tabs) => {
    const run = () => {
      tabs.forEach((t) => {
        if (!sessionStorage.getItem(cacheKey(t))) loadTab(t).catch(() => {});
      });
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1500 });
    else setTimeout(run, 350);
  };

  const go = async (tab, { push = false } = {}) => {
    const next = allowedSet.has(tab) ? tab : "shipping";

    setActiveUI(next);

    if (push) {
      const u = new URL(window.location.href);
      u.pathname = hubPath;
      u.searchParams.set("tab", next);
      window.history.pushState({ tab: next }, "", u.toString());
    }

    await loadTab(next);
    prefetchIdle(allowed.filter((t) => t !== next));
  };

  links.forEach((a) => {
    a.addEventListener("click", (e) => {
      const tab = (a.getAttribute("data-tab-link") || "").toLowerCase();
      if (!allowedSet.has(tab)) return;
      e.preventDefault();
      go(tab, { push: true });
    });
  });

  go(getTab(), { push: false });
  window.addEventListener("popstate", () => go(getTab(), { push: false }));
})();